import { Award, Plus, Search, Edit, Trash2, AlertTriangle, CheckCircle, Clock, Filter, ClipboardList, UserX } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { UnderlineTabsList as TabsList, UnderlineTabsTrigger as TabsTrigger } from "../components/ui/tabs-variants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { useTrainingTracking } from "../hooks/useTrainingTracking";
import { useAuth } from "../context/AuthContext";
import TrainingTab from "../components/settings/TrainingTab";

const STATUS_CONFIG = {
  valid: { label: "Valid", color: "bg-green-100 text-green-800", icon: CheckCircle },
  expiring_soon: { label: "Expiring Soon", color: "bg-amber-100 text-amber-800", icon: Clock },
  expired: { label: "Expired", color: "bg-red-100 text-red-800", icon: AlertTriangle },
};

const TrainingTrackingPage = () => {
  const { getAuthHeaders } = useAuth();
  const {
    groups, totalRecords, employees, certTypes, loading, saving, stats,
    orphaned,
    addOpen, editOpen,
    searchTerm, statusFilter, form,
    setAddOpen, setEditOpen, setSearchTerm, setStatusFilter, setForm,
    openAdd, openEdit, handleAdd, handleEdit, handleDelete, handleDeleteOrphan,
  } = useTrainingTracking();

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr + "T00:00:00");
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const y = d.getFullYear();
    return `${m}/${day}/${y}`;
  };

  const daysUntilExpiry = (dateStr) => {
    const exp = new Date(dateStr + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
  };

  const renderForm = (isEdit) => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Employee *</Label>
        <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
          <SelectTrigger data-testid="training-employee-select">
            <SelectValue placeholder="Select employee..." />
          </SelectTrigger>
          <SelectContent>
            {employees
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Certificate *</Label>
        <Select value={form.certificate_name} onValueChange={(v) => setForm((f) => ({ ...f, certificate_name: v }))}>
          <SelectTrigger data-testid="training-cert-select">
            <SelectValue placeholder="Select certificate..." />
          </SelectTrigger>
          <SelectContent>
            {certTypes
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((ct) => (
                <SelectItem key={ct.id} value={ct.name}>{ct.name}</SelectItem>
              ))}
          </SelectContent>
        </Select>
        {certTypes.length === 0 && (
          <p className="text-xs text-muted-foreground">No certificate types configured. Add them in Settings &gt; Training.</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Training Date *</Label>
        <Input
          type="date"
          value={form.training_date}
          onChange={(e) => setForm((f) => ({ ...f, training_date: e.target.value }))}
          data-testid="training-date-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Validity Period *</Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Years</Label>
            <Select
              value={String(form.validity_years)}
              onValueChange={(v) => setForm((f) => ({ ...f, validity_years: parseInt(v) }))}
            >
              <SelectTrigger data-testid="training-years-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y} year{y !== 1 ? "s" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Months</Label>
            <Select
              value={String(form.validity_months)}
              onValueChange={(v) => setForm((f) => ({ ...f, validity_months: parseInt(v) }))}
            >
              <SelectTrigger data-testid="training-months-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((m) => (
                  <SelectItem key={m} value={String(m)}>{m} month{m !== 1 ? "s" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-enter space-y-6" data-testid="training-tracking-page">
      {/* Header */}
      <div className="page-header flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">Training Tracking</h1>
        </div>
        <Button onClick={openAdd} data-testid="add-training-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Certificate
        </Button>
      </div>

      <Tabs defaultValue="tracking" className="space-y-6">
        <TabsList data-testid="training-tabs">
          <TabsTrigger value="tracking" data-testid="tracking-subtab">
            <Award className="w-4 h-4 mr-2" />Tracking
          </TabsTrigger>
          <TabsTrigger value="certificate-types" data-testid="certificate-types-subtab">
            <ClipboardList className="w-4 h-4 mr-2" />Certificate Types
          </TabsTrigger>
        </TabsList>

        {/* Tracking Tab */}
        <TabsContent value="tracking" className="space-y-6">

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter("all")} data-testid="stat-total">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter("valid")} data-testid="stat-valid">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-sm text-green-600">Valid</p>
            <p className="text-2xl font-bold text-green-700">{stats.valid}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter("expiring_soon")} data-testid="stat-expiring">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-sm text-amber-600">Expiring Soon</p>
            <p className="text-2xl font-bold text-amber-700">{stats.expiring}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter("expired")} data-testid="stat-expired">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-sm text-red-600">Expired</p>
            <p className="text-2xl font-bold text-red-700">{stats.expired}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by employee or certificate..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="training-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="training-status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="valid">Valid</SelectItem>
            <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orphaned Records Report — certs whose employee was deleted entirely */}
      {orphaned.length > 0 && (
        <Card className="border-orange-300 bg-orange-50/60" data-testid="orphaned-records-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-orange-800">
              <UserX className="w-5 h-5" />
              Orphaned Certificates ({orphaned.length})
            </CardTitle>
            <p className="text-sm text-orange-700/80">
              These training records belong to employees who were deleted from the system. They are excluded from stats and notifications, but you can remove them here to keep records tidy.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {orphaned.map((record) => (
                <div
                  key={record.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/70 border border-orange-200"
                  data-testid={`orphaned-record-${record.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {record.employee_name || "Unknown employee"}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {record.certificate_name} · expires {formatDate(record.expiration_date)}
                    </p>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive/70 hover:text-destructive flex-shrink-0"
                        data-testid={`delete-orphaned-${record.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove Orphaned Record?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Delete {record.employee_name || "Unknown"}'s {record.certificate_name} record? The employee no longer exists in the system. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteOrphan(record.id)}
                          className="bg-destructive text-destructive-foreground"
                          data-testid={`confirm-delete-orphaned-${record.id}`}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grouped Certificate Cards */}
      {groups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
            <p className="text-lg text-muted-foreground">
              {totalRecords === 0
                ? "No training records yet. Click 'Add Certificate' to get started."
                : "No records match your filters."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <Card key={group.certificate_name} data-testid={`cert-group-${group.certificate_name}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <Award className="w-5 h-5 text-primary" />
                  {group.certificate_name}
                  <Badge variant="secondary" className="ml-auto">
                    {group.records.length} record{group.records.length !== 1 ? "s" : ""}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* Table header */}
                  <div className="hidden md:grid grid-cols-12 gap-3 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider border-b">
                    <div className="col-span-3">Employee</div>
                    <div className="col-span-2">Trained</div>
                    <div className="col-span-2">Expires</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-1">Days Left</div>
                    <div className="col-span-2 text-right">Actions</div>
                  </div>

                  {group.records.map((record) => {
                    const statusConf = STATUS_CONFIG[record.status] || STATUS_CONFIG.valid;
                    const StatusIcon = statusConf.icon;
                    const days = daysUntilExpiry(record.expiration_date);

                    return (
                      <div
                        key={record.id}
                        className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 p-3 rounded-lg transition-colors ${
                          record.status === "expired"
                            ? "bg-red-50 hover:bg-red-100/70"
                            : record.status === "expiring_soon"
                            ? "bg-amber-50 hover:bg-amber-100/70"
                            : "bg-muted/20 hover:bg-muted/40"
                        }`}
                        data-testid={`training-record-${record.id}`}
                      >
                        {/* Mobile labels + Desktop grid */}
                        <div className="col-span-3 font-medium">{record.employee_name}</div>
                        <div className="col-span-2 text-sm text-muted-foreground">{formatDate(record.training_date)}</div>
                        <div className="col-span-2 text-sm">{formatDate(record.expiration_date)}</div>
                        <div className="col-span-2">
                          <Badge className={`${statusConf.color} gap-1`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConf.label}
                          </Badge>
                        </div>
                        <div className="col-span-1 text-sm font-mono">
                          {days < 0 ? (
                            <span className="text-red-600">{Math.abs(days)}d over</span>
                          ) : (
                            <span className={days <= 45 ? "text-amber-600" : "text-muted-foreground"}>{days}d</span>
                          )}
                        </div>
                        <div className="col-span-2 flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEdit(record)}
                            data-testid={`edit-record-${record.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive/60 hover:text-destructive"
                                data-testid={`delete-record-${record.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Training Record?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Remove {record.employee_name}'s {record.certificate_name} record? This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(record.id)}
                                  className="bg-destructive text-destructive-foreground"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

        </TabsContent>

        {/* Certificate Types Tab */}
        <TabsContent value="certificate-types">
          <TrainingTab getAuthHeaders={getAuthHeaders} />
        </TabsContent>
      </Tabs>

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5" /> Add Training Certificate
            </DialogTitle>
          </DialogHeader>
          {renderForm(false)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving} data-testid="save-training-btn">
              {saving ? "Saving..." : "Add Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" /> Edit Training Certificate
            </DialogTitle>
          </DialogHeader>
          {renderForm(true)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={saving} data-testid="update-training-btn">
              {saving ? "Saving..." : "Update Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingTrackingPage;
